/**
 * BusinessCapabilityForm Component
 *
 * Reusable form for creating and editing business capabilities
 */

import {
  TextField,
  SelectField,
  EntityAutocompleteField,
  EntityArrayAutocompleteField,
  type CreateCapabilityFormData,
} from '../forms';
import { createCapabilitySchema } from '../forms/validation';
import { EntityForm } from '../shared';
import { TagPicker } from '../tags';
import { useAllStakeholders, useBusinessUnits, useCapabilities } from '../../graphql/queries';
import {
  CapabilityCriticality,
  StrategicImportance,
  CapabilityLifecycleStatus,
  InvestmentPriority,
  InvestmentLevel,
} from '../../graphql/types';
import '@tylertech/forge/card';
import '@tylertech/forge/icon';
import './BusinessCapabilityForm.css';

export interface BusinessCapabilityFormProps {
  defaultValues?: Partial<CreateCapabilityFormData>;
  onSubmit: (data: CreateCapabilityFormData) => void | Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
  mode: 'create' | 'edit';
}

const CardElement = 'forge-card' as React.ElementType;

export function BusinessCapabilityForm({
  defaultValues,
  onSubmit,
  onCancel,
  isSubmitting = false,
  mode,
}: BusinessCapabilityFormProps): JSX.Element {
  // Full catalogs (all pages, not one capped server page) so each picker can
  // search/select any existing entity, not just the first page (issue #768).
  // Business Owner / Stakeholders source from the Stakeholder catalog rather
  // than the admin-gated REST `/users` endpoint — same read-scope rationale
  // as the Business Process Owner picker (issue #769).
  const { data: stakeholdersData } = useAllStakeholders();
  const stakeholderOptions = (stakeholdersData?.stakeholders?.edges ?? []).map(({ node }) => ({
    value: node.entityId,
    label: node.name,
  }));

  const { data: businessUnitsData } = useBusinessUnits();
  const businessUnitOptions = (businessUnitsData?.businessUnits?.edges ?? []).map(({ node }) => ({
    value: node.entityId,
    label: node.name,
  }));

  const { data: capabilitiesData } = useCapabilities();
  const parentCapabilityOptions = (capabilitiesData?.capabilities?.edges ?? []).map(({ node }) => ({
    value: node.entityId,
    label: node.name,
  }));

  return (
    <EntityForm<CreateCapabilityFormData>
      schema={createCapabilitySchema}
      baseValues={{
        level: 1,
        criticality: 'important',
        strategicImportance: 'core',
        lifecycleStatus: 'active',
        stakeholderIds: [],
        tags: [],
      }}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      onCancel={onCancel}
      isSubmitting={isSubmitting}
      mode={mode}
      createLabel="Create Capability"
      formClassName="capability-form"
    >
      {({ register, formState: { errors }, values, setValue }) => {
        const stakeholderIds = values.stakeholderIds || [];
        const tags = values.tags || [];

        return (
          <>
            <CardElement className="form-card form-card--surface">
              <div className="form-section form-section--padded">
                <h2 className="forge-typography--headline6">Basic Information</h2>

                <TextField
                  label="Capability Name"
                  {...register('name')}
                  value={values.name || ''}
                  error={errors.name}
                  required
                  placeholder="e.g., Customer Relationship Management"
                  helperText="A descriptive name for the business capability"
                />

                <TextField
                  label="Description"
                  {...register('description')}
                  value={values.description || ''}
                  error={errors.description}
                  placeholder="Brief description of the capability"
                  helperText="Optional description (max 1000 characters)"
                />

                <TextField
                  label="Level"
                  type="number"
                  {...register('level', { valueAsNumber: true })}
                  value={String(values.level ?? 1)}
                  error={errors.level}
                  required
                  helperText="Hierarchy level (1 = top-level)"
                />

                <EntityAutocompleteField
                  name="parentCapabilityId"
                  label="Parent Capability"
                  options={parentCapabilityOptions}
                  value={values.parentCapabilityId || ''}
                  onChange={(entityId) =>
                    setValue('parentCapabilityId', entityId, { shouldValidate: true })
                  }
                  error={errors.parentCapabilityId}
                  placeholder="Search business capabilities…"
                  helperText="Optional parent for hierarchy"
                  emptyMessage="No business capabilities match your search"
                />
              </div>
            </CardElement>

            <CardElement className="form-card form-card--surface">
              <div className="form-section form-section--padded">
                <h2 className="forge-typography--headline6">Classification</h2>

                <SelectField
                  label="Criticality"
                  {...register('criticality')}
                  value={values.criticality}
                  options={[
                    { value: CapabilityCriticality.Critical, label: 'Critical' },
                    { value: CapabilityCriticality.Important, label: 'Important' },
                    { value: CapabilityCriticality.Supporting, label: 'Supporting' },
                    { value: CapabilityCriticality.NiceToHave, label: 'Nice to Have' },
                  ]}
                  error={errors.criticality}
                  required
                />

                <SelectField
                  label="Strategic Importance"
                  {...register('strategicImportance')}
                  value={values.strategicImportance}
                  options={[
                    { value: StrategicImportance.Strategic, label: 'Strategic' },
                    { value: StrategicImportance.Core, label: 'Core' },
                    { value: StrategicImportance.Supporting, label: 'Supporting' },
                  ]}
                  error={errors.strategicImportance}
                  required
                />

                <SelectField
                  label="Lifecycle Status"
                  {...register('lifecycleStatus')}
                  value={values.lifecycleStatus}
                  options={[
                    { value: CapabilityLifecycleStatus.Planned, label: 'Planned' },
                    { value: CapabilityLifecycleStatus.Developing, label: 'Developing' },
                    { value: CapabilityLifecycleStatus.Active, label: 'Active' },
                    { value: CapabilityLifecycleStatus.Mature, label: 'Mature' },
                    { value: CapabilityLifecycleStatus.Deprecated, label: 'Deprecated' },
                    { value: CapabilityLifecycleStatus.Retired, label: 'Retired' },
                  ]}
                  error={errors.lifecycleStatus}
                  required
                />

                <SelectField
                  label="Investment Priority"
                  {...register('investmentPriority')}
                  value={values.investmentPriority || ''}
                  options={[
                    { value: '', label: '— None —' },
                    { value: InvestmentPriority.High, label: 'High' },
                    { value: InvestmentPriority.Medium, label: 'Medium' },
                    { value: InvestmentPriority.Low, label: 'Low' },
                    { value: InvestmentPriority.Hold, label: 'Hold' },
                  ]}
                  error={errors.investmentPriority}
                />
              </div>
            </CardElement>

            <CardElement className="form-card form-card--surface">
              <div className="form-section form-section--padded">
                <h2 className="forge-typography--headline6">Ownership</h2>

                <EntityAutocompleteField
                  name="businessOwnerId"
                  label="Business Owner"
                  options={stakeholderOptions}
                  value={values.businessOwnerId || ''}
                  onChange={(entityId) =>
                    setValue('businessOwnerId', entityId, { shouldValidate: true })
                  }
                  error={errors.businessOwnerId}
                  required
                  placeholder="Search stakeholders…"
                  helperText="The stakeholder responsible for this capability"
                />

                <EntityAutocompleteField
                  name="owningBusinessUnitId"
                  label="Owning Business Unit"
                  options={businessUnitOptions}
                  value={values.owningBusinessUnitId || ''}
                  onChange={(entityId) =>
                    setValue('owningBusinessUnitId', entityId, { shouldValidate: true })
                  }
                  error={errors.owningBusinessUnitId}
                  required
                  placeholder="Search business units…"
                  helperText="The business unit that owns this capability"
                />

                <EntityArrayAutocompleteField
                  name="stakeholderIds"
                  label="Stakeholders"
                  options={stakeholderOptions}
                  value={stakeholderIds}
                  onChange={(ids) => setValue('stakeholderIds', ids)}
                  placeholder="Search stakeholders…"
                  helperText="Additional stakeholders beyond the business owner"
                  emptyMessage="No stakeholders match your search"
                  removeAriaLabel="Remove stakeholder"
                />
              </div>
            </CardElement>

            <CardElement className="form-card form-card--surface">
              <div className="form-section form-section--padded">
                <h2 className="forge-typography--headline6">Maturity &amp; Investment</h2>

                <TextField
                  label="Maturity Level"
                  type="number"
                  {...register('maturityLevel', { valueAsNumber: true })}
                  value={values.maturityLevel != null ? String(values.maturityLevel) : ''}
                  error={errors.maturityLevel}
                  helperText="Current maturity level (1–5)"
                />

                <TextField
                  label="Target Maturity Level"
                  type="number"
                  {...register('targetMaturityLevel', { valueAsNumber: true })}
                  value={
                    values.targetMaturityLevel != null ? String(values.targetMaturityLevel) : ''
                  }
                  error={errors.targetMaturityLevel}
                  helperText="Target maturity level (1–5)"
                />

                <SelectField
                  label="Current Investment Level"
                  {...register('currentInvestmentLevel')}
                  value={values.currentInvestmentLevel || ''}
                  options={[
                    { value: '', label: '— None —' },
                    { value: InvestmentLevel.None, label: 'None' },
                    { value: InvestmentLevel.Maintain, label: 'Maintain' },
                    { value: InvestmentLevel.Enhance, label: 'Enhance' },
                    { value: InvestmentLevel.Transform, label: 'Transform' },
                  ]}
                  error={errors.currentInvestmentLevel}
                />

                <SelectField
                  label="Target Investment Level"
                  {...register('targetInvestmentLevel')}
                  value={values.targetInvestmentLevel || ''}
                  options={[
                    { value: '', label: '— None —' },
                    { value: InvestmentLevel.None, label: 'None' },
                    { value: InvestmentLevel.Maintain, label: 'Maintain' },
                    { value: InvestmentLevel.Enhance, label: 'Enhance' },
                    { value: InvestmentLevel.Transform, label: 'Transform' },
                  ]}
                  error={errors.targetInvestmentLevel}
                />

                <TextField
                  label="Planning Horizon"
                  {...register('planningHorizon')}
                  value={values.planningHorizon || ''}
                  error={errors.planningHorizon}
                  placeholder="e.g., FY2026 Q3"
                  helperText="Optional planning timeframe"
                />
              </div>
            </CardElement>

            <CardElement className="form-card form-card--surface">
              <div className="form-section form-section--padded">
                <h2 className="forge-typography--headline6">Additional Information</h2>

                <TextField
                  label="Notes"
                  {...register('notes')}
                  value={values.notes || ''}
                  error={errors.notes}
                  placeholder="Additional notes about this capability"
                  helperText="Optional notes (max 2000 characters)"
                />

                <TextField
                  label="Assumptions"
                  {...register('assumptions')}
                  value={values.assumptions || ''}
                  error={errors.assumptions}
                  placeholder="Key assumptions about this capability"
                  helperText="Optional assumptions (max 2000 characters)"
                />

                <TagPicker
                  id="business-capability-tag-picker"
                  label="Tags"
                  selectedTagIds={tags}
                  onChange={(newIds) => setValue('tags', newIds)}
                />
              </div>
            </CardElement>
          </>
        );
      }}
    </EntityForm>
  );
}
